import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { PQIndexState } from "./pqIndexTypes";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  CellValueDto,
  PostPqIndexGetApiArg,
  PostPqIndexGetApiResponse,
  PqIndexDimension,
  pqIndexApi,
} from "../../../app/apislices/pqIndexApi";
import { AppDispatch, RootState } from "../../../app/store";

const lastSunday = dayjs().startOf("week").add(-1, "days");

const initialState: PQIndexState = {
  selectedStart: dayjs(lastSunday).add(-6, "days").toISOString(),
  selectedEnd: lastSunday.toISOString(),
  selectedDimensions: ["MeasuringPoints", "Time"],
  selectedSubstation: null,
  selectedDataFieldCategory: null,
  isFetching: false,
  showParameterPanel: true,
  isSubmitted: false,
};

const pqIndexSlice = createSlice({
  name: "pqIndexSlice",
  initialState,
  reducers: {
    setSelectedStart: (state, action: PayloadAction<string>) => {
      state.selectedStart = action.payload;
    },
    setSelectedEnd: (state, action: PayloadAction<string>) => {
      state.selectedEnd = action.payload;
    },
    setSelectedDimensions: (state, action: PayloadAction<PqIndexDimension[]>) => {
      state.selectedDimensions = action.payload;
      state.selectedSubstation = null;
      state.selectedDataFieldCategory = null;
      state.isSubmitted = true;
    },
    setSelectedSubstation: (state, action: PayloadAction<string | null>) => {
      state.selectedSubstation = action.payload;
      state.isSubmitted = true;
    },
    setSelectedDataFieldCategory: (state, action: PayloadAction<string | null>) => {
      state.selectedDataFieldCategory = action.payload;
      state.isSubmitted = true;
    },
    setIsFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    setShowParameterPanel: (state, action: PayloadAction<boolean>) => {
      state.showParameterPanel = action.payload;
    },
    setIsSubmitted: (state, action: PayloadAction<boolean>) => {
      state.isSubmitted = action.payload;
    },
  },
});

export const selectSelectedVerticalDimension = (state: RootState) => state.pqIndex.selectedDimensions[0];
export const selectSelectedHorizontalDimension = (state: RootState) => state.pqIndex.selectedDimensions[1];

export const {
  setSelectedStart,
  setSelectedEnd,
  setSelectedDimensions,
  setSelectedSubstation,
  setSelectedDataFieldCategory,
  setIsFetching,
  setShowParameterPanel,
  setIsSubmitted,
} = pqIndexSlice.actions;

export default pqIndexSlice.reducer;

export const getDataOnDimensionChange =
  (dimension: PqIndexDimension[]) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    dispatch(setIsFetching(true));

    const state = getState();
    const selectedStart = state.pqIndex.selectedStart;
    const selectedEnd = state.pqIndex.selectedEnd;
    const selectedMeasuringPoints = state.customAutocomplete.instances["pqIndexMeasuringPoints"].selected;
    const selectedDataFields = state.customAutocomplete.instances["pqIndexDataFields"].selected;

    dispatch(setIsSubmitted(true));

    const result = dispatch(
      pqIndexApi.endpoints.postPqIndexGet.initiate({
        row: dimension[0],
        column: dimension[1],
        groupBySubstation: true,
        groupByCategory: true,
        pqIndexRequestDto: {
          start: selectedStart,
          end: selectedEnd,
          measuringPointNames: selectedMeasuringPoints?.map((x) => x.name),
          dataFieldIds: selectedDataFields?.map((x) => x.id),
          thresholdStandard: "IEC 61000-3-6",
          pqIndexMinimum: -0.1,
          pqIndexMaximum: 1,
        },
      })
    );
    result
      .unwrap()
      .then(() => {
        dispatch(setSelectedDimensions(dimension));
        result.unsubscribe();
      })
      .finally(() => dispatch(setIsFetching(false)));
  };

export const getDataOnSubstationChange =
  (substation: string | null) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    dispatch(setIsFetching(true));

    const state = getState();
    const selectedStart = state.pqIndex.selectedStart;
    const selectedEnd = state.pqIndex.selectedEnd;
    const selectedMeasuringPoints = state.customAutocomplete.instances["pqIndexMeasuringPoints"].selected;
    const selectedDataFields = state.customAutocomplete.instances["pqIndexDataFields"].selected;
    const selectedDataFieldCategory = state.pqIndex.selectedDataFieldCategory;

    const result = dispatch(
      pqIndexApi.endpoints.postPqIndexGet.initiate({
        row: state.pqIndex.selectedDimensions[0],
        column: state.pqIndex.selectedDimensions[1],
        groupBySubstation: !substation,
        groupByCategory: !selectedDataFieldCategory,
        pqIndexRequestDto: {
          start: selectedStart,
          end: selectedEnd,
          measuringPointNames: substation
            ? selectedMeasuringPoints?.filter((x) => x.substation === substation).map((x) => x.name)
            : selectedMeasuringPoints?.map((x) => x.name),
          dataFieldIds: selectedDataFieldCategory
            ? selectedDataFields?.filter((x) => x.pqIndexCategory === selectedDataFieldCategory).map((x) => x.id)
            : selectedDataFields?.map((x) => x.id),
          thresholdStandard: "IEC 61000-3-6",
          pqIndexMinimum: -0.1,
          pqIndexMaximum: 1,
        },
      })
    );
    result
      .unwrap()
      .then(() => {
        dispatch(setSelectedSubstation(substation));
        result.unsubscribe();
      })
      .finally(() => dispatch(setIsFetching(false)));
  };

export const getDataOnDataFieldCategoryChange =
  (dataFieldCategory: string | null) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    dispatch(setIsFetching(true));

    const state = getState();
    const selectedStart = state.pqIndex.selectedStart;
    const selectedEnd = state.pqIndex.selectedEnd;
    const selectedMeasuringPoints = state.customAutocomplete.instances["pqIndexMeasuringPoints"].selected;
    const selectedDataFields = state.customAutocomplete.instances["pqIndexDataFields"].selected;
    const selectedSubstation = state.pqIndex.selectedSubstation;

    const result = dispatch(
      pqIndexApi.endpoints.postPqIndexGet.initiate({
        row: state.pqIndex.selectedDimensions[0],
        column: state.pqIndex.selectedDimensions[1],
        groupBySubstation: !selectedSubstation,
        groupByCategory: !dataFieldCategory,
        pqIndexRequestDto: {
          start: selectedStart,
          end: selectedEnd,
          measuringPointNames: selectedSubstation
            ? selectedMeasuringPoints?.filter((x) => x.substation === selectedSubstation).map((x) => x.name)
            : selectedMeasuringPoints?.map((x) => x.name),
          dataFieldIds: dataFieldCategory
            ? selectedDataFields?.filter((x) => x.pqIndexCategory === dataFieldCategory).map((x) => x.id)
            : selectedDataFields?.map((x) => x.id),
          thresholdStandard: "IEC 61000-3-6",
          pqIndexMinimum: -0.1,
          pqIndexMaximum: 1,
        },
      })
    );
    result
      .unwrap()
      .then(() => {
        dispatch(setSelectedDataFieldCategory(dataFieldCategory));
        result.unsubscribe();
      })
      .finally(() => dispatch(setIsFetching(false)));
  };

pqIndexApi.enhanceEndpoints({
  endpoints: {
    postPqIndexGet: {
      transformResponse: (response: PostPqIndexGetApiResponse, meta: unknown, args: PostPqIndexGetApiArg) => {
        if (args.column === "Time") {
          const start = dayjs(args.pqIndexRequestDto.start).startOf("week");
          const end = dayjs(args.pqIndexRequestDto.end).startOf("week").add(1, "week");
          let currentDate = start;
          let currentColumn = 0;

          dayjs.extend(customParseFormat);

          return response.map((row) => {
            const newCells: CellValueDto[] = [];
            while (currentDate.isBefore(end)) {
              if (currentColumn >= row.length || currentDate.isBefore(dayjs(row[currentColumn].col, "MM/DD/YYYY HH:mm:ss")))
                newCells.push({ col: currentDate.format("MM/DD/YYYY HH:mm:ss"), row: row[0].row, value: null });
              else newCells.push(row[currentColumn++]);
              currentDate = currentDate.add(1, "week");
            }
            currentDate = start;
            currentColumn = 0;
            return newCells;
          });
        } else return response;
      },
    },
  },
});
